import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Suspense, lazy, useEffect } from "react";
import { PageTransition } from "./PageTransition";
import { BottomNav } from "./BottomNav";
import { Skeleton } from "@/components/ui/skeleton";
import { usePrefetchRoutes } from "@/hooks/usePrefetch";

// Eager load critical pages
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";

// Lazy load non-critical pages for better initial load time
const Taxi = lazy(() => import("@/pages/Taxi"));
const Food = lazy(() => import("@/pages/Food"));
const Restaurant = lazy(() => import("@/pages/Restaurant"));
const Cart = lazy(() => import("@/pages/Cart"));
const OrderStatus = lazy(() => import("@/pages/OrderStatus"));
const History = lazy(() => import("@/pages/History"));
const Profile = lazy(() => import("@/pages/Profile"));
const Settings = lazy(() => import("@/pages/Settings"));
const Delivery = lazy(() => import("@/pages/Delivery"));
const Admin = lazy(() => import("@/pages/Admin"));
const Support = lazy(() => import("@/pages/Support"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Install = lazy(() => import("@/pages/Install"));
const Presentation = lazy(() => import("@/pages/Presentation"));
const Performance = lazy(() => import("@/pages/Performance"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background p-4 pt-20">
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-20 w-full rounded-xl" />
    </div>
  </div>
);

// Pages that should show bottom navigation
const pagesWithNav = ["/", "/food", "/cart", "/history", "/profile", "/taxi", "/delivery", "/settings", "/support", "/restaurant", "/order", "/favorites"];

export const AnimatedRoutes = () => {
  const location = useLocation();
  usePrefetchRoutes(); // Prefetch popular routes
  
  const showNav = pagesWithNav.some(path => 
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path)
  );

  return (
    <>
      <AnimatePresence mode="wait">
        <Suspense fallback={<PageLoader />}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageTransition><Index /></PageTransition>} />
            <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
            <Route path="/taxi" element={<PageTransition><Taxi /></PageTransition>} />
            <Route path="/food" element={<PageTransition><Food /></PageTransition>} />
            <Route path="/restaurant/:id" element={<PageTransition><Restaurant /></PageTransition>} />
            <Route path="/cart" element={<PageTransition><Cart /></PageTransition>} />
            <Route path="/order/:id" element={<PageTransition><OrderStatus /></PageTransition>} />
            <Route path="/delivery" element={<PageTransition><Delivery /></PageTransition>} />
            <Route path="/history" element={<PageTransition><History /></PageTransition>} />
            <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
            <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
            <Route path="/admin" element={<PageTransition><Admin /></PageTransition>} />
            <Route path="/support" element={<PageTransition><Support /></PageTransition>} />
            <Route path="/favorites" element={<PageTransition><Favorites /></PageTransition>} />
            <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
            <Route path="/terms" element={<PageTransition><TermsOfService /></PageTransition>} />
            <Route path="/presentation" element={<PageTransition><Presentation /></PageTransition>} />
            <Route path="/install" element={<PageTransition><Install /></PageTransition>} />
            <Route path="/performance" element={<PageTransition><Performance /></PageTransition>} />
            <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
          </Routes>
        </Suspense>
      </AnimatePresence>
      
      {/* BottomNav is outside AnimatePresence to stay fixed during page transitions */}
      {showNav && <BottomNav />}
    </>
  );
};
